import React, { useRef, useEffect, useState, ReactElement } from 'react'
import * as d3 from 'd3'
import useWindowSize from 'hooks/useWindowSize'
import { v4 as uuidv4 } from 'uuid'
import { TweenMax, Power1 } from 'gsap'
import { data, groups } from './data'
import Scrollchor from 'react-scrollchor'
import './styles.scss'
import { schemeDark2 } from 'd3'

interface Props {
  onTextOpen: (title: string, content: string, links: Array<string>) => void
  isTextPanelOpen: boolean
}

interface Cnode {
  data: any
  node: any
}

interface Position {
  x: number
  y: number
}

export const TypescriptComponent = ({ onTextOpen, isTextPanelOpen }: Props) => {
  const canvas = useRef<HTMLDivElement>(null)
  const svg = useRef<SVGSVGElement>(null)
  const infoContainer = useRef<HTMLDivElement>(null)
  const canvasScroll = useRef<ReactElement>(null)
  const articleScroll = useRef<ReactElement>(null)
  const containerSize = useWindowSize()
  const canvasDimensions = {
    width: containerSize.width,
    height: containerSize.height
  }
  const nodeSize = 40
  const strokeSize = 5
  const simulation = useRef<any>(null)
  const link = useRef<any>(null)
  const node = useRef<any>(null)
  const graph = useRef<any>(
    JSON.parse(window.localStorage.getItem('graph')) || { ...data }
  )
  const scene = useRef<any>(null)
  const scenePosition = useRef<Position>({ x: 0, y: 0 })
  const activeNode = useRef<any>(
    JSON.parse(window.localStorage.getItem('activeNode'))
      ? graph.current.nodes.find(
          (n: any) =>
            n.id === JSON.parse(window.localStorage.getItem('activeNode')).id
        )
      : null
  )
  const touchDownTimer = useRef<number | null>(null)
  const allowClick = useRef(true)
  const [allowDrag, setAllowDrag] = useState(false)
  const [showStartHintText, setShowStartHintText] = useState(true)
  const isDragging = useRef(false)
  const startDragPosition = useRef<Position>({ x: 0, y: 0 })
  const startScenePosition = useRef<Position>({ x: 0, y: 0 })
  const clickedNode = useRef<Cnode>({
    data: null,
    node: null
  })
  const isMobile = window.innerWidth < 500
  const nodesState = JSON.parse(window.localStorage.getItem('states')) || {}

  // Animation frame
  const ticked = () => {
    // Update the links position
    if (link.current) {
      link.current
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y)
    }

    // Update the nodes position
    if (node.current) {
      node.current.attr(
        'transform',
        (d: any) => 'translate(' + d.x + ',' + d.y + ')'
      )
    }

    // Change the camera position to follow the clicked node but
    // only if the user isn't draging. Use a tween to have a smooth movement.
    if (!isDragging.current) {
      if (activeNode.current) {
        TweenMax.to(scenePosition.current, 1, {
          x: canvasDimensions.width / 2 - activeNode.current.x,
          y: canvasDimensions.height / 2 - activeNode.current.y,
          ease: Power1.easeOut
        })
      }
      if (scene.current) {
        scene.current.attr(
          'transform',
          (d: any) =>
            'translate(' +
            scenePosition.current.x +
            ',' +
            scenePosition.current.y +
            ')'
        )
      }
    }
  }

  const touchStartHandler = function(data: any, el: any) {
    clickedNode.current.data = data
    clickedNode.current.node = el

    if (!data.disabled) {
      clickedNode.current.node.classed('touchstart', true)
      touchDownTimer.current = window.setTimeout(() => {
        allowClick.current = false
        infoContainer.current.classList.add('closed')
        onTextOpen(data.title, data.text, data.links)
      }, 750)
    } else {
      onTextOpen(data.title, data.text, data.links)
    }

    update()
  }

  const update = function() {
    if (link.current) {
      link.current = link.current
        .data(graph.current.links)
        .join('line')
        .attr('class', (d: any) => 'color-' + d.group)
        .attr('stroke-width', strokeSize)
    }

    if (node.current) {
      node.current = node.current
        .data(graph.current.nodes)
        .join(function(enter: any) {
          const circleGroup = enter
            .append('g')
            .attr('class', (d: any) => {
              let c = 'circle'
              if (d.disabled) c += ' disabled'
              if (d.visited) c += ' visited'
              c += ' type-' + d.type
              return c
            })
            .on('mousedown touchstart', function(d: any) {
              touchStartHandler(d, d3.select(d3.event.currentTarget))
            })
            .on('mouseup touchend', function(d: any) {
              // Dispatch mouseup event
              clearTimeout(touchDownTimer.current)
              clickedNode.current.node.classed('touchstart', false)
            })
          // Circle shadow
          circleGroup
            .append('circle')
            .attr('class', 'stroke')
            .attr('r', (d: any) => nodeSize * d.size * 1.25)
          // Circle body
          circleGroup
            .append('circle')
            .attr('class', 'out')
            .attr('r', (d: any) => nodeSize * d.size)
          // Visual feedback
          circleGroup
            .append('circle')
            .attr('class', 'mask')
            .attr('r', (d: any) => nodeSize * d.size * 0.9 - 2)
          // Circle picture
          circleGroup
            .append('image')
            .attr('class', 'eye open')
            .attr('xlink:href', (d: any) => require('../../images/' + d.icon))
            .attr('x', (d: any) => -nodeSize * d.size)
            .attr('y', (d: any) => -nodeSize * d.size)
            .attr('width', (d: any) => nodeSize * d.size * 2)
            .attr('height', (d: any) => nodeSize * d.size * 2)
          // Circle label
          circleGroup
            .append('g')
            .attr('class', 'title')
            .attr(
              'transform',
              (d: any) => `translate(${0}, ${nodeSize * d.size + 25})`
            )
            .append('text')
            .attr('class', 'stroke')
            .text((d: any) => d.title)
            .attr('x', 0)
            .attr('y', 0)
            .clone(true)
            .attr('class', 'fill')
          // Circle type icon
          circleGroup
            .append('image')
            .attr('class', (d: any) => {
              return 'type' + (d.visited ? ' visited' : '')
            })
            .attr('xlink:href', (d: any) =>
              require('../../images/type-' +
                d.type +
                (d.visited ? '-visited-' + (d.isOpen ? 'close' : 'open') : '') +
                '.svg')
            )
            .attr('x', 20)
            .attr('y', -40)
            .attr('width', 30)
            .attr('height', 30)
          return circleGroup
        })
    }

    simulation.current.nodes(graph.current.nodes)
    simulation.current.force('link').links(graph.current.links)
  }

  const dragStartHandler = (d: any) => {
    console.log('Start drag')
    // Save the position of the pointer before the drag
    startDragPosition.current = {
      x: d3.event.x,
      y: d3.event.y
    }

    // Save the position of the scene before the drag
    startScenePosition.current = scenePosition.current

    // Tell to the animation ticker that we don't want to
    // animate the scene position as we are dragging it manually
    isDragging.current = true
  }

  const dragHandler = (d: any) => {
    // Clear the timer: we don't want to open the text
    // anymore as we are dragging the scene
    if (touchDownTimer.current !== 0) {
      clearTimeout(touchDownTimer.current)
    }

    // We don't want to allow the user to develop a node
    // after the dragging is over
    if (allowClick.current) {
      allowClick.current = false
    }

    // Calculate the new scene position according
    // to the user interaction
    scenePosition.current = {
      x:
        startScenePosition.current.x + d3.event.x - startDragPosition.current.x,
      y: startScenePosition.current.y + d3.event.y - startDragPosition.current.y
    }

    // Apply the new position to the scene node
    scene.current.attr(
      'transform',
      `translate(
              ${scenePosition.current.x},
              ${scenePosition.current.y})`
    )
  }

  const expandNode = d => {
    const diffX = d.x - canvasDimensions.width / 2
    const diffY = d.y - canvasDimensions.height / 2
    const angle = Math.atan2(diffY, diffX) // / Math.PI * 180
    const angleInDegrees =
      ((angle > 0 ? angle : 2 * Math.PI + angle) * 360) / (2 * Math.PI)
    const corrAngleInDegrees = 360 - angleInDegrees - 90
    let direction = 1

    if (diffX < 0 && diffY < 0) {
      direction = 1
      // angle  = 90
      // console.log('Quadrant A')
    } else if (diffX > 0 && diffY < 0) {
      direction = -1
      /// angle  = 0
      // console.log('Quadrant B')
    } else if (diffX > 0 && diffY > 0) {
      direction = -1
      // angle = 90
      // console.log('Quadrant C')
    } else if (diffX < 0 && diffY > 0) {
      direction = 1
      // angle = 90
      // console.log('Quadrant D')
    }

    // If this node as children nodes, add them to the simulation
    if (groups[d.nid]) {
      simulation.current.stop()

      // Get the new nodes and assign them a unique id and a position
      const newNodes = groups[d.nid].map((n, i) => {
        const angleOffset =
          direction * i * (360 / groups[d.nid].length) +
          corrAngleInDegrees +
          20 * direction
        // console.log(i, n.title, angleOffset)

        return {
          ...n,
          id: uuidv4(),
          x: d.x + Math.sin((angleOffset * Math.PI) / 180) * 80,
          y: d.y + Math.cos((angleOffset * Math.PI) / 180) * 80,
          visited: nodesState[n.nid]
        }
      })

      // console.log(nodesState)
      // console.log(d.nid)
      // console.log(nodesState[d.nid])

      // Set the new links
      const newLinks = newNodes.reduce(
        (accumulator, currentValue) => [
          ...accumulator,
          {
            source: currentValue.id,
            target: d.id,
            group: nodesState[currentValue.nid] ? 1 : -1
          }
        ],
        []
      )

      // Add the new nodes and links to the graph data
      graph.current.nodes = [...graph.current.nodes, ...newNodes]
      graph.current.links = [...graph.current.links, ...newLinks]

      // Redraw everything and update the simulation with the new data
      update()
      simulation.current.alpha(1).restart()
    }
  }

  // Remove all the children of a node from the simulation
  const closeNode = d => {
    simulation.current.stop()

    // Which nodes directly linked to the parent have to be closed
    let nodesToClose = groups[d.nid].reduce((a, b) => [...a, b.nid], [])

    // Go up the three and close the other groups
    nodesToClose.forEach(node => {
      nodesToClose = groups[node].reduce((a, b) => [...a, b.nid], nodesToClose)
    })

    // Keep only the remaining links and nodes
    const remainingNodes = graph.current.nodes.filter(
      n => !nodesToClose.includes(n.nid)
    )
    const remainingLinks = graph.current.links.filter(
      n => !nodesToClose.includes(n.source.nid)
    )

    // Remove the nodes and links from the graph data
    graph.current.nodes = remainingNodes
    graph.current.links = remainingLinks

    // Redraw everything and update the simulation with the new data
    update()
    simulation.current.alpha(1).restart()
  }

  // This is where the click events are managed
  const dragEndHandler = (d: any) => {
    console.log('Stop drag')

    // Clear the timer that should open the text panel
    clearTimeout(touchDownTimer.current)

    if (
      d3.event.x - startDragPosition.current.x > 5 ||
      d3.event.y - startDragPosition.current.y > 5
    ) {
      console.log('REAL DRAG')
    }

    // Remove the touchdown indicator
    if (clickedNode.current.node) {
      clickedNode.current.node.classed('touchstart', false)
    }

    // When the user click on a node (mouseUp, touchEnd events)
    if (allowClick.current && clickedNode.current.data) {
      // Get the data from the node
      const node = clickedNode.current

      // If it's the main node, just do nothing
      if (node.data.id === 0) return 0

      // If this node can be expanded and is has not been visited
      if (groups[node.data.nid].length > 0 && !node.data.visited) {
        // Center the camera on the node
        activeNode.current = node.data

        // Expand this node
        expandNode(node.data)

        node.data.isOpen = true

        // Allow the drag again
        isDragging.current = false
        // If this node has already been visited we want to be able to close it
      } else if (node.data.visited && node.data.isOpen) {
        // Center the camera on the parent node
        activeNode.current = node.data
        // console.log(groups.filter(g => {const n= g.reduce((a, b) => [...a, b.nid], []); return g.includes(node.data)}))
        for (const [key, value] of Object.entries(groups)) {
          const nids = value.reduce((a, b) => [...a, b.nid], [])
          if (nids.includes(node.data.nid)) {
            activeNode.current = graph.current.nodes.find(n => n.nid === +key)
          }
        }

        closeNode(node.data)

        node.data.isOpen = false

        // Allow the drag again
        isDragging.current = false
      } else if (node.data.visited && !node.data.isOpen) {
        // Center the camera on the node
        activeNode.current = node.data

        expandNode(node.data)

        node.data.isOpen = true

        // Allow the drag again
        isDragging.current = false
      }

      // The link and node are now visited
      if (graph.current.links.length > 0) {
        graph.current.links.find(
          (n: any) => n.source.id === node.data.id
        ).group = node.data.group
      }
      node.data.visited = 1
      nodesState[node.data.nid] = true

      // Update the state image to reflect the visited state
      node.node
        .select('image.type')
        .attr('xlink:href', (d: any) =>
          require('../../images/type-' +
            d.type +
            '-visited-' +
            (d.isOpen ? 'close' : 'open') +
            '.svg')
        )
      node.node.classed('visited', true)
    }

    allowClick.current = true
    clickedNode.current.data = null
    clickedNode.current.node = null
    update()

    // Save the graph locally
    const dataToStore = { ...graph.current }
    dataToStore.links = dataToStore.links.reduce(
      (a, b) => [
        ...a,
        { source: b.source.id, target: b.target.id, group: b.group }
      ],
      []
    )
    window.localStorage.setItem('states', JSON.stringify(nodesState))
    window.localStorage.setItem('graph', JSON.stringify(dataToStore))
    window.localStorage.setItem(
      'activeNode',
      JSON.stringify(activeNode.current)
    )
  }

  // When the window is resized
  useEffect(() => {
    console.log('CANVAS DIMENSIONS UPDATED')
    console.log('>', 'x', canvasDimensions.width, 'y', canvasDimensions.height)

    if (simulation.current) {
      console.log('> Update simulation center')
      simulation.current
        .force(
          'center',
          d3.forceCenter(
            canvasDimensions.width / 2,
            canvasDimensions.height / 2
          )
        )
        .on('tick', ticked)

      update()
      simulation.current.alpha(1).restart()
    }
  }, [canvasDimensions.width, canvasDimensions.height])

  useEffect(() => {
    console.log('ALLOW DRAG', allowDrag)
  }, [allowDrag])

  // When the SVG is ready
  useEffect(() => {
    const svgNode = d3.select(svg.current)

    // Empty the SVG
    svgNode.select('.scene').remove()

    // Initialize the simulation
    simulation.current = d3
      .forceSimulation()
      .force(
        'link',
        d3
          .forceLink()
          .id((d: any) => d.id)
          // .distance((d: any) => 1)
          .strength(0.15)
      )
      .force(
        'charge',
        d3
          .forceManyBody()
          .distanceMax(900)
          .strength((d: any) => -600)
      )
    /* .force(
        'collision',
        d3.forceCollide().radius(d => nodeSize * d.size * 2)
      ) */

    // We need to initiate a drag event on the whole SVG node
    if (!isMobile) {
      svgNode.call(
        d3
          .drag<any>()
          .on('start', dragStartHandler)
          .on('drag', dragHandler)
          .on('end', dragEndHandler)
      )
    }

    // Create a group for the graph
    scene.current = svgNode.append('g').attr('class', 'scene')

    // Create a group for the links (under the nodes)
    link.current = scene.current
      .append('g')
      .attr('class', 'links')
      .selectAll('line')

    // Create a group for the nodes (over the lines)
    node.current = scene.current
      .append('g')
      .attr('class', 'nodes')
      .selectAll('g')
  }, [svg])

  return (
    <>
      <div className='ui vertical segment canvas-infos'>
        <div className='ui text container'>
          <span className='colored'>Cliquez sur les</span>
          <svg className='dummy-circle' width='24' height='24'>
            <g transform='translate(12, 12)'>
              <circle className='stroke' />
              <circle className='out' />
              <circle className='in' />
              <circle className='mask' />
            </g>
          </svg>
          pour <span className='colored'>développer</span>{' '}
          <img src={require('../../images/dummy-type-1.svg')} /> un thème ou{' '}
          <span className='colored'>lire</span>{' '}
          <img src={require('../../images/dummy-type-2.svg')} /> son contenu.
          Vous pouvez vous déplacer dans le réseau comme vous le feriez pour une
          carte. Les
          <span className='dummy-line' />
          <span className='colored'>lignes de couleur</span> vous indiquent les
          thèmes que vous avez déjà explorés.
        </div>
      </div>
      <div
        id='canvas'
        className={'canvas' + (isTextPanelOpen ? ' text-open' : '')}
        ref={canvas}
        style={{ height: canvasDimensions.height }}
      >
        {!allowDrag && isMobile && <div className='events-catcher' />}
        <Scrollchor ref={canvasScroll} to='canvas' />
        <Scrollchor ref={articleScroll} to='article-next' />
        <div className='gradient top' />
        <div className='gradient bottom' />
        {showStartHintText && (
          <div className='dummy-infos-container'>
            <div className='dummy-infos'>
              Touchez pour <span className='colored'>commencer</span>
              <img className='arrow' src={require('../../images/arrow.svg')} />
            </div>
            <div
              className='dummy-touch-circle'
              onClick={() => {
                console.log('Allow drag = true')
                setAllowDrag(true)
                d3.select(svg.current).call(
                  d3
                    .drag<any>()
                    .on('start', dragStartHandler)
                    .on('drag', dragHandler)
                    .on('end', dragEndHandler)
                )
                canvasScroll.current.simulateClick()
                if (graph.current.nodes.length === 1)
                  expandNode(graph.current.nodes[0])
                setShowStartHintText(false)
              }}
            >
              <div className='pulse-circle' />
              <div
                className='first-circle'
                style={{
                  backgroundImage: `url(${require('../../images/maudet.png')})`
                }}
              />
            </div>
            <div className='dummy-label'>L'affaire Maudet</div>
          </div>
        )}
        {!showStartHintText && (
          <div className='infos-container'>
            <div className='content'>
              <div
                className='ui small primary button labeled icon'
                onClick={(e: any) => {
                  // e.stopPropagation()
                  // The graph data is now the origin data
                  graph.current = { ...data }
                  // The center point is the first node
                  activeNode.current = graph.current.nodes[0]
                  // Reset the scene position
                  scenePosition.current = { x: 0, y: 0 }
                  // Apply the new position to the scene node
                  scene.current.attr(
                    'transform',
                    `translate(
                        ${scenePosition.current.x},
                        ${scenePosition.current.y})`
                  )
                  // Redraw everything
                  update()
                  // Restart the simulation
                  simulation.current.alpha(1).restart()
                  // empty the local storage
                  window.localStorage.clear()
                  setShowStartHintText(true)
                }}
              >
                Recommencer <i className='ui icon undo' />
              </div>
            </div>
          </div>
        )}
        {!showStartHintText && (
          <div className='bottom-container'>
            <div className='content'>
              {isMobile && allowDrag && (
                <div>
                  <div
                    className='ui primary small button labeled icon'
                    onClick={() => {
                      console.log('Allow drag = false')
                      setAllowDrag(false)
                      setShowStartHintText(true)
                      /* d3.select(svg.current)
                        .on('.drag', null)
                        .on('touchstart', null) */
                      d3.select(svg.current).call(
                        d3
                          .drag<any>()
                          .on('start', null)
                          .on('drag', null)
                          .on('end', null)
                      )
                      articleScroll.current.simulateClick()
                    }}
                  >
                    Continuer la lecture <i className='ui icon chevron down' />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <svg
          ref={svg}
          width={canvasDimensions.width}
          height={canvasDimensions.height}
        />
      </div>
    </>
  )
}
